import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layouts/AdminLayout';
import { toast } from 'sonner';
import {
  Plus,
  Edit2,
  Trash2,
  Camera,
  Video,
  BookOpen,
  TrendingUp,
  Globe,
  Palette,
  Folder,
  ToggleLeft,
  ToggleRight,
  Save,
  X
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const ServicesPage = () => {
  const { api } = useAuth();
  const [categories, setCategories] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [packageDialogOpen, setPackageDialogOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [currentPackage, setCurrentPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    icon: 'folder',
    description: '',
    active: true
  });
  const [packageFormData, setPackageFormData] = useState({
    service_category: '',
    name: '',
    description: '',
    features: [''],
    base_price: 0
  });

  const iconOptions = [
    { id: 'camera', label: 'Camera', icon: Camera },
    { id: 'video', label: 'Video', icon: Video },
    { id: 'book-open', label: 'Book', icon: BookOpen },
    { id: 'trending-up', label: 'Trending', icon: TrendingUp },
    { id: 'globe', label: 'Globe', icon: Globe },
    { id: 'palette', label: 'Palette', icon: Palette },
    { id: 'folder', label: 'Folder', icon: Folder }
  ];

  const getIconComponent = (iconId) => {
    const iconOption = iconOptions.find(opt => opt.id === iconId);
    return iconOption?.icon || Folder;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [categoriesRes, packagesRes] = await Promise.all([
        api.get('/service-categories'),
        api.get('/packages')
      ]);
      setCategories(categoriesRes.data);
      setPackages(packagesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = () => {
    setCurrentCategory(null);
    setFormData({ name: '', icon: 'folder', description: '', active: true });
    setEditDialogOpen(true);
  };

  const handleEditCategory = (category) => {
    setCurrentCategory(category);
    setFormData({
      name: category.name,
      icon: category.icon,
      description: category.description || '',
      active: category.active
    });
    setEditDialogOpen(true);
  };

  const handleSaveCategory = async () => {
    try {
      if (currentCategory) {
        await api.patch(`/service-categories/${currentCategory.id}`, formData);
        toast.success('Service updated');
      } else {
        await api.post('/service-categories', formData);
        toast.success('Service created');
      }
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save category:', error);
      toast.error(error.response?.data?.detail || 'Failed to save service');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!confirm('Are you sure you want to delete this service?')) return;
    try {
      await api.delete(`/service-categories/${categoryId}`);
      toast.success('Service deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete category:', error);
      toast.error('Failed to delete service');
    }
  };

  const handleToggleActive = async (category) => {
    try {
      await api.patch(`/service-categories/${category.id}`, { active: !category.active });
      toast.success(category.active ? 'Service deactivated' : 'Service activated');
      fetchData();
    } catch (error) {
      console.error('Failed to toggle category:', error);
      toast.error('Failed to update service');
    }
  };

  const handleCreatePackage = (categoryId) => {
    setCurrentPackage(null);
    setPackageFormData({
      service_category: categoryId || '',
      name: '',
      description: '',
      features: [''],
      base_price: 0
    });
    setPackageDialogOpen(true);
  };

  const handleEditPackage = (pkg) => {
    setCurrentPackage(pkg);
    setPackageFormData({
      service_category: pkg.service_category,
      name: pkg.name,
      description: pkg.description || '',
      features: pkg.features || [''],
      base_price: pkg.base_price
    });
    setPackageDialogOpen(true);
  };

  const handleSavePackage = async () => {
    try {
      if (currentPackage) {
        // For now, packages don't have a PATCH endpoint, so we skip edit
        toast.info('Package update coming soon');
      } else {
        await api.post('/packages', packageFormData);
        toast.success('Package created');
      }
      setPackageDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save package:', error);
      toast.error('Failed to save package');
    }
  };

  const addFeature = () => {
    setPackageFormData({
      ...packageFormData,
      features: [...packageFormData.features, '']
    });
  };

  const updateFeature = (index, value) => {
    const newFeatures = [...packageFormData.features];
    newFeatures[index] = value;
    setPackageFormData({ ...packageFormData, features: newFeatures });
  };

  const removeFeature = (index) => {
    setPackageFormData({
      ...packageFormData,
      features: packageFormData.features.filter((_, i) => i !== index)
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getCategoryPackages = (categoryId) => {
    return packages.filter(p => p.service_category === categoryId);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white" data-testid="services-page-title">Service Management</h1>
            <p className="text-fp-textMuted text-sm mt-1">Manage service categories and packages</p>
          </div>
          <Button onClick={handleCreateCategory} className="btn-primary" data-testid="add-service-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Service
          </Button>
        </div>

        {/* Services Grid */}
        {loading ? (
          <div className="text-center py-12 text-fp-textMuted">Loading...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((category) => {
              const IconComponent = getIconComponent(category.icon);
              const categoryPackages = getCategoryPackages(category.id);
              
              return (
                <motion.div
                  key={category.id}
                  className={`bg-fp-surface rounded-xl border ${category.active ? 'border-white/10' : 'border-red-500/30'} overflow-hidden`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid={`service-card-${category.id}`}
                >
                  {/* Category Header */}
                  <div className={`p-6 ${!category.active ? 'opacity-50' : ''}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                        <IconComponent className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleActive(category)}
                          className={`p-1 rounded ${category.active ? 'text-green-400' : 'text-red-400'}`}
                          title={category.active ? 'Deactivate' : 'Activate'}
                        >
                          {category.active ? (
                            <ToggleRight className="w-6 h-6" />
                          ) : (
                            <ToggleLeft className="w-6 h-6" />
                          )}
                        </button>
                        <button
                          onClick={() => handleEditCategory(category)}
                          className="p-2 hover:bg-white/10 rounded text-fp-textMuted hover:text-white"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {!category.is_default && (
                          <button
                            onClick={() => handleDeleteCategory(category.id)}
                            className="p-2 hover:bg-red-500/20 rounded text-fp-textMuted hover:text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <h3 className="text-lg font-semibold text-white mb-2">{category.name}</h3>
                    <p className="text-fp-textMuted text-sm line-clamp-2">
                      {category.description || 'No description'}
                    </p>
                    
                    {category.is_default && (
                      <span className="inline-block mt-3 px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded">
                        Default Service
                      </span>
                    )}
                  </div>

                  {/* Packages Section */}
                  <div className="border-t border-white/10 p-4 bg-fp-bg/50">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-fp-textMuted text-sm">
                        {categoryPackages.length} Package{categoryPackages.length !== 1 ? 's' : ''}
                      </span>
                      <button
                        onClick={() => handleCreatePackage(category.id)}
                        className="text-primary text-sm hover:underline flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" />
                        Add Package
                      </button>
                    </div>
                    
                    {categoryPackages.length > 0 ? (
                      <div className="space-y-2">
                        {categoryPackages.map((pkg) => (
                          <div 
                            key={pkg.id}
                            className="p-3 bg-fp-surface rounded-lg flex items-center justify-between cursor-pointer hover:bg-white/5"
                            onClick={() => handleEditPackage(pkg)}
                          >
                            <div>
                              <p className="text-white text-sm font-medium">{pkg.name}</p>
                              <p className="text-primary text-sm">{formatCurrency(pkg.base_price)}</p>
                            </div>
                            <Edit2 className="w-4 h-4 text-fp-textMuted" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-fp-textMuted text-sm text-center py-2">No packages yet</p>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Category Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="bg-fp-surface border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">
                {currentCategory ? 'Edit Service' : 'Add Service'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-fp-textMuted text-xs block mb-2">Service Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Wedding Photography"
                  className="bg-fp-bg border-white/10 text-white"
                  disabled={currentCategory?.is_default}
                  data-testid="service-name-input"
                />
              </div>
              
              <div>
                <label className="text-fp-textMuted text-xs block mb-2">Icon</label>
                <div className="grid grid-cols-7 gap-2">
                  {iconOptions.map((option) => {
                    const IconComp = option.icon;
                    return (
                      <button
                        key={option.id}
                        onClick={() => setFormData({ ...formData, icon: option.id })}
                        className={`p-3 rounded-lg border transition-colors ${
                          formData.icon === option.id
                            ? 'border-primary bg-primary/20'
                            : 'border-white/10 hover:border-white/20'
                        }`}
                        title={option.label}
                      >
                        <IconComp className={`w-5 h-5 ${formData.icon === option.id ? 'text-primary' : 'text-fp-textMuted'}`} />
                      </button>
                    );
                  })}
                </div>
              </div>
              
              <div>
                <label className="text-fp-textMuted text-xs block mb-2">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe this service..."
                  className="bg-fp-bg border-white/10 text-white resize-none"
                  rows={3}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-fp-bg rounded-lg">
                <span className="text-white text-sm">Active</span>
                <button
                  onClick={() => setFormData({ ...formData, active: !formData.active })}
                  className={formData.active ? 'text-green-400' : 'text-red-400'}
                >
                  {formData.active ? (
                    <ToggleRight className="w-8 h-8" />
                  ) : (
                    <ToggleLeft className="w-8 h-8" />
                  )}
                </button>
              </div>
              
              <div className="flex gap-3 justify-end pt-4">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="border-white/10">
                  Cancel
                </Button>
                <Button onClick={handleSaveCategory} className="btn-primary" data-testid="save-service-btn">
                  <Save className="w-4 h-4 mr-2" />
                  Save Service
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Package Edit Dialog */}
        <Dialog open={packageDialogOpen} onOpenChange={setPackageDialogOpen}>
          <DialogContent className="bg-fp-surface border-white/10 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">
                {currentPackage ? 'Edit Package' : 'Add Package'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-fp-textMuted text-xs block mb-2">Service Category *</label>
                <Select 
                  value={packageFormData.service_category} 
                  onValueChange={(value) => setPackageFormData({ ...packageFormData, service_category: value })}
                >
                  <SelectTrigger className="bg-fp-bg border-white/10 text-white">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="bg-fp-surface border-white/10">
                    {categories.filter(c => c.active).map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-fp-textMuted text-xs block mb-2">Package Name *</label>
                <Input
                  value={packageFormData.name}
                  onChange={(e) => setPackageFormData({ ...packageFormData, name: e.target.value })}
                  placeholder="e.g., Basic Package"
                  className="bg-fp-bg border-white/10 text-white"
                />
              </div>
              
              <div>
                <label className="text-fp-textMuted text-xs block mb-2">Description</label>
                <Textarea
                  value={packageFormData.description}
                  onChange={(e) => setPackageFormData({ ...packageFormData, description: e.target.value })}
                  placeholder="Describe this package..."
                  className="bg-fp-bg border-white/10 text-white resize-none"
                  rows={2}
                />
              </div>
              
              <div>
                <label className="text-fp-textMuted text-xs block mb-2">Base Price (INR) *</label>
                <Input
                  type="number"
                  value={packageFormData.base_price}
                  onChange={(e) => setPackageFormData({ ...packageFormData, base_price: parseFloat(e.target.value) || 0 })}
                  placeholder="0"
                  className="bg-fp-bg border-white/10 text-white"
                />
              </div>
              
              <div>
                <label className="text-fp-textMuted text-xs block mb-2">Features</label>
                {packageFormData.features.map((feature, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <Input
                      value={feature}
                      onChange={(e) => updateFeature(index, e.target.value)}
                      placeholder="Feature description"
                      className="bg-fp-bg border-white/10 text-white"
                    />
                    <button
                      onClick={() => removeFeature(index)}
                      className="p-2 hover:bg-red-500/20 text-red-400 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <Button
                  onClick={addFeature}
                  variant="outline"
                  size="sm"
                  className="border-white/10 text-fp-textMuted hover:text-white"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add Feature
                </Button>
              </div>
              
              <div className="flex gap-3 justify-end pt-4">
                <Button variant="outline" onClick={() => setPackageDialogOpen(false)} className="border-white/10">
                  Cancel
                </Button>
                <Button onClick={handleSavePackage} className="btn-primary" data-testid="save-package-btn">
                  <Save className="w-4 h-4 mr-2" />
                  Save Package
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default ServicesPage;
